
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ChatMessage, toMessage } from "@/types/chat";
import { useToast } from "@/components/ui/use-toast";
import { useSubscriptionService } from "@/services/subscriptionService";

export const useChat = (videoId: string) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState<string>("");
  const { toast } = useToast();
  const { canSendMessage, incrementMessageUsage } = useSubscriptionService();

  // Load existing chat history for this video
  useEffect(() => {
    const fetchChatHistory = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from("chat_history")
          .select("*")
          .eq("user_id", user.id)
          .eq("video_id", videoId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error("Error fetching chat history:", error);
          setError("Failed to load chat history");
          setIsLoading(false);
          return;
        }

        if (data) {
          // Convert database messages to ChatMessage format
          if (data.messages) {
            const dbMessages = data.messages as any[];
            if (Array.isArray(dbMessages)) {
              const formattedMessages: ChatMessage[] = dbMessages.map((msg: any) => ({
                id: msg.id?.toString() || crypto.randomUUID(),
                role: msg.role || (msg.isUser ? 'user' : 'assistant'),
                content: msg.content || msg.text || '',
                timestamp: msg.timestamp || new Date().toISOString()
              }));
              setMessages(formattedMessages);
            }
          }
          setSessionId(data.session_id);
        } else {
          // Create a new session ID for this chat
          const newSessionId = crypto.randomUUID();
          setSessionId(newSessionId);
          
          // Initialize with empty messages array
          setMessages([]);
          
          // Create a new chat history record
          await supabase.from("chat_history").insert({
            user_id: user.id,
            video_id: videoId,
            session_id: newSessionId,
            messages: []
          });
        }
      } catch (err) {
        console.error("Error in fetchChatHistory:", err);
        setError("Failed to load chat history");
      } finally {
        setIsLoading(false);
      }
    };

    if (videoId) {
      fetchChatHistory();
    }
  }, [videoId]);

  // Save messages to the database whenever they change
  useEffect(() => {
    const saveChatHistory = async () => {
      try {
        if (!sessionId || !videoId || messages.length === 0) return;

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Convert ChatMessage[] to a JSON-serializable format
        const serializedMessages = messages.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp
        }));

        await supabase
          .from("chat_history")
          .update({
            messages: serializedMessages,
            updated_at: new Date().toISOString()
          })
          .eq("session_id", sessionId)
          .eq("user_id", user.id)
          .eq("video_id", videoId);
      } catch (error) {
        console.error("Error saving chat history:", error);
      }
    };

    saveChatHistory();
  }, [messages, sessionId, videoId]);

  const sendMessage = useCallback(
    async (content: string) => {
      try {
        // Check if user can send a message based on their subscription
        const canSend = await canSendMessage(videoId);
        if (!canSend) {
          throw new Error("Message limit reached. Please upgrade to continue chatting.");
        }

        // Add user message to state
        const userMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "user",
          content: content,
          timestamp: new Date().toISOString(),
        };

        setMessages((prevMessages) => [...prevMessages, userMessage]);
        setIsLoading(true);
        setError("");

        // Increment message usage for this video
        await incrementMessageUsage(videoId);

        // Send message to the AI
        console.log("Sending message to chat-with-video function:", {
          videoId,
          message: content,
          sessionId,
        });
        
        const response = await supabase.functions.invoke("chat-with-video", {
          body: {
            videoId,
            message: content,
            sessionId,
          },
        });

        console.log("Response from chat-with-video:", response);

        if (response.error) {
          throw new Error(response.error.message || "Failed to get AI response");
        }

        const aiResponse = response.data;
        
        if (!aiResponse) {
          throw new Error("Received empty response from AI");
        }

        // Add AI response to state
        const aiMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: aiResponse.message || aiResponse.response || 
                  (typeof aiResponse === 'string' ? aiResponse : "Sorry, I couldn't process that request."),
          timestamp: new Date().toISOString(),
        };

        setMessages((prevMessages) => [...prevMessages, aiMessage]);
      } catch (err) {
        console.error("Error sending message:", err);
        setError(err instanceof Error ? err.message : "Failed to send message");
        toast({
          title: "Error",
          description: err instanceof Error ? err.message : "Failed to get a response. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [videoId, sessionId, toast, canSendMessage, incrementMessageUsage]
  );

  return {
    messages,
    isLoading,
    error,
    sendMessage,
  };
};
